import React, { useState, useCallback } from 'react';
import block from 'propmods';
import { Button, Tooltip } from 'lego-on-react';
import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Access';
import { useCurrentAliceApp } from '../../../hooks/useCurrentAliceApp';
import { useCopy } from '../../../hooks/useCopy';
import { useFetchingHandler } from '../../../hooks/useFetchingHandler';
import { shareApi } from '../../../api';
import { FormError } from '../../Form/FormError';

import './LinksPage.scss';

const t = i18n(keyset);
const b = block('LinksPage');

export const LinksPage: React.FC = () => {
    const skill = useCurrentAliceApp()!;
    const [oneTimeLink, setOneTimeLink] = useState('');
    const { buttonRef, getTooltipProps, onButtonClick, onCodeClick, textRef } = useCopy();

    const copyTooltipProps = getTooltipProps();

    const [createOneTimeShare, generationState] = useFetchingHandler({
        handler: shareApi.createOneTimeShare,
        throwErrors: true,
    });

    const generateOneTimeLink = useCallback(async () => {
        try {
            const oneTimeShareKey = await createOneTimeShare({ skillId: skill.id }, skill.channel);

            const shareLink = `${__appConfig.app.shareBaseUrl}?key=${oneTimeShareKey}`;

            setOneTimeLink(shareLink);
        } catch (e) {
            console.error(e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skill.id]);

    const isGenerating = generationState === 'loading';
    const isError = generationState === 'error';

    return (
        <div {...b()}>
            <h2 {...b('header')}>{t('links-header')}</h2>
            <p {...b('description')}>{t('links-descr')}</p>
            <div
                {...b('one-time-link', { disabled: isGenerating })}
                ref={textRef}
                onClick={isGenerating ? undefined : onCodeClick}
            >
                {oneTimeLink}
            </div>
            <div {...b('buttons')}>
                <div {...b('generate-button')}>
                    <Button theme="action" size="s" onClick={generateOneTimeLink} progress={isGenerating}>
                        {t('links-button-generate')}
                    </Button>
                </div>
                <div {...b('copy-button')}>
                    <Button
                        theme="normal"
                        size="s"
                        onClick={onButtonClick}
                        ref={buttonRef}
                        disabled={!oneTimeLink || isGenerating}
                    >
                        {t('links-button-copy')}
                    </Button>
                </div>
                {copyTooltipProps.visible && (
                    <Tooltip {...copyTooltipProps} theme="white" autoclosable to="right" size="s">
                        {t('links-button-copy-tooltip')}
                    </Tooltip>
                )}
            </div>
            {isError && (
                <div {...b('error')}>
                    <FormError>{t('error')}</FormError>
                </div>
            )}
        </div>
    );
};
